import { BYOItem, BYOStep, BYOStepId, ContentType } from 'api/contentful/contentTypes.cloverdotcom';
import { BYOStepOrder } from 'features/build-your-own/BuildYourOwnModule';
import { useDCSProductsQuery } from 'hooks/DCS/useDCSProductsQuery';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { getObjectKeyLength } from 'utils/Object';
import type { CurrentStepData } from '../useBuildYourOwn';
import {
  filteredAccessoriesForDevicesAndSoftwarePlan,
  filteredDevicesForSoftwarePlan,
  filteredPosNeedsForBusinessOffering,
  getAllProductDcsIdsWithQuantities,
  isDevicesStepComplete,
  isMultipleQuantityAllowed,
  selectionReducer,
  BYO_SELECTION_STATE_KEY,
} from './utils';
import { useDCSSoftwarePlansQuery } from 'hooks/DCS/useDCSSoftwarePlansQuery';
import {
  getProductsTotalUpfrontPrice,
  getProductsWithoutPlansTotalUpfrontPrice,
  getSoftwareMonthlyPricing,
} from 'utils/DCSUtils';
import { useReseller } from 'hooks/Reseller/useReseller';
import { BooleanFilter } from 'utils/TypeUtils';
import { getCartProductsForAccessories, getItemsByType } from '../utils';
import { SessionStorage } from 'utils/SessionStorage';
import { useCartContext } from 'components/Cart/hooks/useCartContext';

export enum BYOBusinessOffering {
  FOOD_AND_DRINKS = 'FOOD_AND_DRINKS',
  RETAIL_OR_SERVICES = 'RETAIL_OR_SERVICES',
  RETAIL_AND_SERVICES = 'RETAIL_AND_SERVICES',
  RETAIL = 'RETAIL',
  SERVICES = 'SERVICES',
}

export type ByoSelectionItem = {
  quantity: number;
  itemKey: string;
  dcsIds: string[];
  supportedSoftwarePlanDcsIds: string[];
  headlineTitle: string;
  secondaryTitle: string;
  contentType: ContentType;
};

export type BYOSelectionState = Record<BYOStepId, Record<string, ByoSelectionItem>> & {
  [BYOStepId.BUSINESS_OFFERING]: Record<string, ByoSelectionItem>;
  [BYOStepId.POS_NEEDS]: Record<string, ByoSelectionItem>;
  [BYOStepId.DEVICES]: Record<string, ByoSelectionItem>;
  [BYOStepId.ACCESSORIES]: Record<string, ByoSelectionItem>;
  [BYOStepId.ALL_COMPATIBLE_ACCESSORIES]: Record<string, ByoSelectionItem>;
};

export const initialSelectionState = {
  [BYOStepId.BUSINESS_OFFERING]: {},
  [BYOStepId.POS_NEEDS]: {},
  [BYOStepId.DEVICES]: {},
  [BYOStepId.ACCESSORIES]: {},
  [BYOStepId.ALL_COMPATIBLE_ACCESSORIES]: {},
};

/**
 * Hook which handles Build-Your-Own (BYO) selectionState storage, updates, and
 * retrieval getters
 */
export const useBYOSelections = (
  defaultSupportedSoftwarePlanDcsIds: string[],
  currentStepData?: CurrentStepData,
  posNeedsStepData?: BYOStep,
) => {
  const {
    data: dcsProducts,
    error: hasDCSProductsError,
    isLoading: isDCSProductsLoading,
  } = useDCSProductsQuery();
  const {
    data: dcsSoftwarePlans = [],
    error: hasDCSSoftwarePlansError,
    isLoading: isDCSSoftwarePlansLoading,
  } = useDCSSoftwarePlansQuery();
  const { dcsResellerError } = useReseller();
  const { setByoUpsellItems } = useCartContext();

  const [showMaxQuantityModal, setShowMaxQuantityModal] = useState(false);
  const storedSelectionState =
    (SessionStorage.tryGetJSONItem(BYO_SELECTION_STATE_KEY) as BYOSelectionState) ?? initialSelectionState;
  const [selectionState, dispatchSelectionState] = useReducer(selectionReducer, storedSelectionState);

  useEffect(() => {
    SessionStorage.trySetJSONItem(BYO_SELECTION_STATE_KEY, selectionState);
  }, [selectionState]);

  // sets the BYO cart upsell Items if the selectionState is updated
  useEffect(() => {
    const selectedAccessories = getItemsByType(selectionState.ACCESSORIES);
    const allAccessories = getItemsByType(selectionState.ALL_COMPATIBLE_ACCESSORIES);
    const selectedAccessoriesItemKeys = selectedAccessories.map(accessory => accessory.itemKey);
    const removeSelectedAccessoriesFromAllAccessories = allAccessories.filter(
      item => !selectedAccessoriesItemKeys.includes(item.itemKey),
    );

    const upsellItems = getCartProductsForAccessories(
      removeSelectedAccessoriesFromAllAccessories,
      dcsProducts,
    );

    setByoUpsellItems(upsellItems);
  }, [selectionState, dcsProducts]);

  const tryAddSoftwarePlanFromQueryParam = useCallback(
    (softwarePlanParamValue: string | null) => {
      const softwarePlanItem = posNeedsStepData?.items?.find(item => item.itemKey === softwarePlanParamValue);

      if (!softwarePlanItem) return false;

      dispatchSelectionState({
        type: 'ADD',
        stepId: BYOStepId.POS_NEEDS,
        item: softwarePlanItem,
      });
      return true;
    },
    [posNeedsStepData],
  );

  /**
   * Dispatches an action to update a BYO item in the selectionState
   *
   * @param stepId: BYO stepId to be updated
   * @param item: BYO item to be updated in selectionState
   * @param isRemoval: whether the item is being removed (via a decrement or deselect)
   * @returns void
   */
  const handleItemUpdate = (stepId: BYOStepId, item: BYOItem, isRemoval = false) => {
    if (!item.itemKey) return;
    const maxItemSelections = 9;

    const isRemovalOrShouldDeselectItem =
      isRemoval || (isItemSelected(stepId, item.itemKey) && !isMultipleQuantityAllowed(stepId));

    if (isRemovalOrShouldDeselectItem) {
      return dispatchSelectionState({ type: 'REMOVE', stepId, item });
    }

    const anyProductReachedMax = item.products?.reduce((hasReachedMax, product) => {
      if (productQuantity(product.dcsId) >= maxItemSelections) {
        return true;
      }
      return hasReachedMax;
    }, false);

    if (anyProductReachedMax) {
      return setShowMaxQuantityModal(true);
    }

    dispatchSelectionState({ type: 'ADD', stepId, item });
  };

  const removeAllCompatibleAccessories = () => {
    dispatchSelectionState({ type: 'REMOVE_ALL', stepId: BYOStepId.ALL_COMPATIBLE_ACCESSORIES, item: {} });
  };

  const addAllCompatibleAccessories = (items: BYOItem[]) => {
    if (items.length === 0) return;

    items.forEach(item => {
      if (!item.itemKey || !item.products) return;

      dispatchSelectionState({ type: 'ADD', stepId: BYOStepId.ALL_COMPATIBLE_ACCESSORIES, item });
    });
  };

  /**
   * Returns whether a BYO item is selected in the selectionState
   *
   * @param stepId: BYO stepId to read from in the selectionState
   * @param itemKey: itemKey to read from in the selectionState
   * @returns true if the item is present in selectionState, false otherwise
   */
  const isItemSelected = (stepId: BYOStepId, itemKey: string) => {
    return !!selectionState[stepId][itemKey];
  };

  /**
   * Returns quantity of a BYO item in the selectionState
   *
   * @param stepId: BYO stepId to read from in the selectionState
   * @param itemKey: itemKey to read from in the selectionState
   * @returns quantity of the item in the selectionState, 0 if not present
   */
  const itemQuantity = (stepId: BYOStepId, itemKey: string) => {
    return selectionState[stepId][itemKey]?.quantity || 0;
  };

  /**
   * Returns filtered items for the stepId based on previous selectionState
   *
   * @param stepId: BYO stepId we want to filter items for
   * @returns filtered items for the stepId
   */
  const filteredItemsForStep = (stepId?: BYOStepId) => {
    const validStepItems =
      currentStepData?.items?.filter(
        item => !item.products || item.products.every(product => product.dcsId),
      ) ?? [];

    if (!validStepItems.length || !stepId) {
      return [];
    }

    const previousStepId = BYOStepOrder[BYOStepOrder.indexOf(stepId) - 1];
    const previousStepSelections = selectionState[previousStepId];
    const selectedSoftwarePlanDcsId = Object.values(selectionState[BYOStepId.POS_NEEDS])[0]?.dcsIds[0];

    const filterForPosNeedsStep = () => {
      const { FOOD_AND_DRINKS, RETAIL_OR_SERVICES, RETAIL_AND_SERVICES, RETAIL, SERVICES } =
        BYOBusinessOffering;
      const foodAndDrinksWasSelected = BooleanFilter(previousStepSelections[FOOD_AND_DRINKS]);
      const retailSelected = BooleanFilter(previousStepSelections[RETAIL]);
      const retailAndServices = BooleanFilter(
        previousStepSelections[RETAIL] && previousStepSelections[SERVICES],
      );
      const servicesSelected = BooleanFilter(previousStepSelections[SERVICES]);

      if (foodAndDrinksWasSelected) {
        return filteredPosNeedsForBusinessOffering(FOOD_AND_DRINKS, validStepItems, previousStepSelections);
      }

      switch (!foodAndDrinksWasSelected) {
        case retailAndServices:
          return filteredPosNeedsForBusinessOffering(
            RETAIL_AND_SERVICES,
            validStepItems,
            previousStepSelections,
          );
        case retailSelected:
          return filteredPosNeedsForBusinessOffering(RETAIL, validStepItems, previousStepSelections);
        case servicesSelected:
          return filteredPosNeedsForBusinessOffering(SERVICES, validStepItems, previousStepSelections);
        default:
          return filteredPosNeedsForBusinessOffering(
            RETAIL_OR_SERVICES,
            validStepItems,
            previousStepSelections,
            defaultSupportedSoftwarePlanDcsIds,
          );
      }
    };

    const isDcsProductAndSoftwarePlan = dcsProducts && selectedSoftwarePlanDcsId;

    const filterForDevicesStep = () => {
      return isDcsProductAndSoftwarePlan
        ? filteredDevicesForSoftwarePlan(selectedSoftwarePlanDcsId, validStepItems, dcsProducts)
        : [];
    };

    const filterForAccessoriesStep = () => {
      return isDcsProductAndSoftwarePlan
        ? filteredAccessoriesForDevicesAndSoftwarePlan(
            selectedSoftwarePlanDcsId,
            Object.values(selectionState[BYOStepId.DEVICES]),
            validStepItems,
            dcsProducts,
          )
        : [];
    };

    switch (stepId) {
      case BYOStepId.POS_NEEDS:
        return filterForPosNeedsStep();
      case BYOStepId.DEVICES:
        return filterForDevicesStep();
      case BYOStepId.ACCESSORIES:
        return filterForAccessoriesStep();
      default:
        return validStepItems;
    }
  };

  /**
   * Returns whether or not a step is completed based on the selectionState
   *
   * @param stepId: BYO stepId to check if completed
   * @returns true if step is completed, false otherwise
   */
  const isStepCompleted = (stepId: BYOStepId) => {
    const stepSelectionState = selectionState[stepId];

    if (!stepSelectionState) {
      return false;
    }

    const numItemsSelected = getObjectKeyLength(stepSelectionState);

    switch (stepId) {
      case BYOStepId.BUSINESS_OFFERING:
        return numItemsSelected > 0;
      case BYOStepId.POS_NEEDS:
        return numItemsSelected === 1;
      case BYOStepId.DEVICES:
        return isDevicesStepComplete(selectionState, numItemsSelected);
      case BYOStepId.ACCESSORIES:
        return true;
      case BYOStepId.ALL_COMPATIBLE_ACCESSORIES:
        return true;
    }
  };

  /**
   * Returns an array of objects representing the DCS IDs and corresponding quantities for a given BYO step.
   *
   * @param {BYOStepId} BYOStep - The ID of the BYO step.
   * @returns {Array<{ dcsId: string, quantity: number }>} - An array of objects containing DCS ID and quantity information.
   */
  const getDcsIdsToQuantity = useCallback(
    (BYOStep: BYOStepId) => {
      const selectionItems = Object.values(selectionState[BYOStep]);

      return selectionItems.reduce<{ dcsId: string; quantity: number }[]>((acc, deviceItem) => {
        const deviceItemDcsIdToQuantity = deviceItem.dcsIds.map(dcsId => ({
          dcsId,
          quantity: deviceItem.quantity,
        }));

        deviceItemDcsIdToQuantity.forEach(({ dcsId, quantity }) => {
          const existingItemIndex = acc.findIndex(item => item.dcsId === dcsId);

          if (existingItemIndex !== -1) {
            acc[existingItemIndex].quantity += quantity;
          } else {
            acc.push({ dcsId, quantity });
          }
        });

        return acc;
      }, []);
    },
    [selectionState],
  );

  /**
   * Returns a function that, when called, computes and returns an array of selected products.
   * Each product is an object containing its id (dcsId), quantity, and a flag indicating
   * whether it is included in multi device pricing (isIncludedInMultiDevicePricing).
   *
   * The selected products are derived from the dcsProducts list and the quantities from the getDcsIdsToQuantity function.
   * Note that the product list only contains products that are in the selection state.
   *
   * @callback getSelectedProductsWithQuantityAndMultiDeviceFlag
   * @param {Array} dcsProducts - A list of all DCS products in the selection state. Each product should be an object with at least two properties: id and isIncludedInMultiDevicePricing.
   * @param {function} getDcsIdsToQuantity - A function that returns an object mapping DCS ids to quantities.
   * @returns {function(): Array} - A function that, when called, performs the computation and returns the resulting product array.
   */
  const getSelectedProductsWithQuantityAndMultiDeviceFlag = useCallback(() => {
    return getAllProductDcsIdsWithQuantities(getDcsIdsToQuantity)
      .map(({ dcsId, quantity }) => {
        const matchingDcsProduct = dcsProducts?.find(({ id }) => id === dcsId);
        if (!matchingDcsProduct) return;

        return {
          dcsId: matchingDcsProduct.id,
          quantity,
          isIncludedInMultiDevicePricing: matchingDcsProduct.isIncludedInMultiDevicePricing,
        };
      })
      .filter(BooleanFilter);
  }, [dcsProducts, getDcsIdsToQuantity]);

  /**
   * Returns the quantity of a product with a specified dcsId.
   *
   * @param {string} [dcsId] - The dcsId of the product to get the quantity for.
   * @returns {number} The quantity of the product. Returns 0 if the product is not found.
   */
  const productQuantity = useCallback(
    (dcsId?: string) => {
      const matchingProduct = getSelectedProductsWithQuantityAndMultiDeviceFlag().find(
        product => product?.dcsId === dcsId,
      );
      return matchingProduct?.quantity ?? 0;
    },
    [getSelectedProductsWithQuantityAndMultiDeviceFlag],
  );

  const softwarePlanCost = useMemo(() => {
    const softwareDcsId = Object.values(selectionState[BYOStepId.POS_NEEDS])[0]?.dcsIds[0] || '';
    const productsWithQuantityAndMultiDeviceFlag = getSelectedProductsWithQuantityAndMultiDeviceFlag();

    return getSoftwareMonthlyPricing(softwareDcsId, dcsSoftwarePlans, productsWithQuantityAndMultiDeviceFlag);
  }, [selectionState, dcsSoftwarePlans, getSelectedProductsWithQuantityAndMultiDeviceFlag]);

  const selectedSoftwarePlanAdditionalMonthlyFee = useMemo(() => {
    const softwareDcsId = Object.values(selectionState[BYOStepId.POS_NEEDS])[0]?.dcsIds[0] || '';
    const dcsSoftwarePlan = dcsSoftwarePlans.find(({ id }) => id === softwareDcsId);
    return dcsSoftwarePlan?.pricing?.additionalDevicePrice.value;
  }, [selectionState, dcsSoftwarePlans]);

  const totalHardwareCost = useMemo(() => {
    const devicesAndAccessoriesDcsIdToQuantity = getAllProductDcsIdsWithQuantities(getDcsIdsToQuantity);

    const allProducts = devicesAndAccessoriesDcsIdToQuantity.reduce((totalCost, dcsIdToQuantity) => {
      const productsTotalUpfrontPriceOriginal = getProductsTotalUpfrontPrice(
        [dcsIdToQuantity.dcsId],
        dcsProducts,
      );
      return totalCost + productsTotalUpfrontPriceOriginal * dcsIdToQuantity.quantity;
    }, 0);

    const productsWithoutPlans = devicesAndAccessoriesDcsIdToQuantity.reduce((totalCost, dcsIdToQuantity) => {
      const productsTotalUpfrontPriceWithoutPlans = getProductsWithoutPlansTotalUpfrontPrice(
        [dcsIdToQuantity.dcsId],
        dcsProducts,
      );
      return totalCost + productsTotalUpfrontPriceWithoutPlans * dcsIdToQuantity.quantity;
    }, 0);

    return {
      allProducts,
      productsWithoutPlans,
    };
  }, [dcsProducts, getDcsIdsToQuantity]);

  return {
    selectionState,
    dispatchSelectionState,
    tryAddSoftwarePlanFromQueryParam,
    handleItemUpdate,
    isItemSelected,
    itemQuantity,
    filteredItemsForStep,
    showMaxQuantityModal,
    setShowMaxQuantityModal,
    isStepCompleted,
    softwarePlanCost,
    selectedSoftwarePlanAdditionalMonthlyFee,
    totalHardwareCost,
    addAllCompatibleAccessories,
    removeAllCompatibleAccessories,
    hasDCSError: !!hasDCSProductsError || !!hasDCSSoftwarePlansError || !!dcsResellerError,
    isDCSLoading: isDCSProductsLoading || isDCSSoftwarePlansLoading,
  };
};
